// PickerField — the CLOSED FACE of a picker and the hosting of its inside
// (ADR-080 §10 · §11): ONE engine for every list that filters or selects —
// the field is a BUTTON showing what is chosen; it opens the desk PANEL (a
// popover under the field) or the phone SHEET (bottom for a short list, full
// screen for a longer one), both holding the one inside (`PickerList` or the
// `Navigator`, rendered by the host through the `children` slot). Typing on
// the closed field opens it with the letters forwarded to the search box; a
// single pick closes it; the picked value never fights an input. Props in,
// events out: the host (`Picker`, the filter kit) owns the open state, the
// typed text and the NAMED chosen — this primitive never fetches or resolves a
// name.
//
// The closed face (ADR-080 §10): one value — its name (+ context only to tell
// two apart), empty = the question, a clear-✕ only when optional; several in a
// form — chips with ✕ below the field, folded past 5 ("+N more" opens the
// list); a count where space is tight or the chosen show elsewhere; NOTHING
// (`none`) where the chosen have a face of their own beside the field — an
// ordered chain's list, one ✕ per person, no count, no clear-all; a value no
// longer available — a "Not available" chip with ✕. The trigger is a React
// Aria Button on the shared `field-frame` (border · hover · focus light-up ·
// the invalid border by `data-invalid` — a button may not carry
// `aria-invalid` or `aria-required`; the error text is linked by
// `aria-describedby`), NAMED by its label + a hidden "Required" + the value it
// shows (`aria-labelledby`), so the chosen name, the count and the marks are
// announced with the field. The face keys on the `md` tier; a story
// FORCES it with `face`. The desk panel is React Aria's Popover + Dialog;
// the phone face is `Sheet`. The foundation owns focus, Escape, dismiss, the
// overlay stack and RTL; we own 100 % of the markup + tokens.

import { useId, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Button as AriaButton, Dialog, DialogTrigger, Popover as AriaPopover } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { Button } from './Button.js';
import { icons } from './icons.js';
import { PHONE_QUERY, Sheet, type SheetFace } from './Sheet.js';
import { TagGroup } from './TagGroup.js';

/** Every field carries a name — a visible string `label`, a ReactNode label WITH its words as `ariaLabel`, or an `ariaLabel` alone — enforced by the type: the panel's and the sheet's title are built from the words (a ReactNode label cannot be read into them); the trigger is named by the label element itself. */
export type PickerFieldName =
  | { label: string; ariaLabel?: string }
  | {
      /** A ReactNode label (a marker beside the words) — keep any adornment `aria-hidden`. */
      label: ReactNode;
      ariaLabel: string;
    }
  | { label?: undefined; ariaLabel: string };

/** A chosen value as the field shows it — named by the host (the hook), never resolved here. */
export interface PickerChosen {
  id: string;
  label: string;
  /** Context under the name on the closed face of a pick-one field — a deep tree's PATH ("Mount Lebanon / Aley" under
   *  the picked area — the areas above it, never the country); small text, one line. */
  description?: string;
  /** The source answered absent (outside the viewer's scope, gone, of another kind): the chip reads "Not available". */
  unavailable?: boolean;
  /** No longer choosable (a disabled value, a decommissioned site): shown, marked. */
  inactive?: boolean;
  /** A setting waiting for its switch — kept and marked "Dormant" (ADR-080 §9). */
  dormant?: boolean;
}

/** The chips fold past this many (ADR-080 §10). */
export const CHIPS_SHOWN = 5;

interface PickerFieldCommon {
  /** The NAMED chosen values, in order. */
  chosen: readonly PickerChosen[];
  /** CONTROLLED: the host owns the open state (and seeds its typed text from `typed`). */
  open: boolean;
  /** `typed` is set when a printable key on the closed field opened it — the letters to forward into the search box. */
  onOpenChange: (open: boolean, typed?: string) => void;
  /** The inside — `PickerList` or the `Navigator` — given `close` and the face it is hosted in (`phone` = finger-sized rows). */
  children: (api: { close: () => void; face: 'desk' | 'phone' }) => ReactNode;
  /** The phone face: a short list from the bottom, a longer one full screen (the host knows the list's size). Default `full`. */
  phoneFace?: SheetFace;
  /** Forces the host face (stories; the gate runs at one viewport). Default: the `md` tier. */
  face?: 'desk' | 'phone';
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** A clear-✕ beside the trigger once something is chosen — optional fields and nullable filters only. */
  clearable?: boolean;
  /** The rule in words, under the field. */
  help?: ReactNode;
  className?: string;
  /** The field's name in its form (`data-field` on the trigger) — what a host that opens a drawer ON a field
   *  (`DrawerForm`'s `autoFocusField`) finds it by, as it finds a native input by `name`. */
  name?: string;
}

export type PickerFieldProps = PickerFieldCommon &
  PickerFieldName &
  (
    | { multiple?: false; onClear?: () => void; selectedDisplay?: 'name' }
    | {
        multiple: true;
        onClear?: () => void;
        /** How the closed face reflects the chosen: `count` (compact — a filter, a tight space), `tags` (chips below the
         *  field — a form) or `none` (the chosen have their own face beside the field — an ordered chain's list; the box
         *  always asks, no clear-✕). Default `count`. */
        selectedDisplay?: 'count' | 'tags' | 'none';
        /** Remove one chosen value (the chip's ✕). */
        onRemove?: (id: string) => void;
        /** Accessible name of the chip group. */
        selectedItemsLabel?: string;
      }
  );

const triggerBase =
  'w-full px-3 py-2 text-sm pointer-coarse:text-base rounded-md outline-none transition-colors field-frame ' +
  'flex items-center justify-between gap-2 text-start cursor-pointer ' +
  'bg-[var(--color-bg-page)] text-[var(--color-text-primary)] ' +
  'data-[disabled]:bg-[var(--color-bg-surface-alt)] data-[disabled]:text-[var(--color-text-disabled)] data-[disabled]:cursor-not-allowed';

export function PickerField(props: PickerFieldProps) {
  const { chosen, open, onOpenChange, children, phoneFace = 'full', face, placeholder, required = false, error, disabled = false, clearable = false, help, className = '', label, ariaLabel, name } = props;
  const { t } = useTranslation('common');
  const reactId = useId();
  const buttonId = `${reactId}-trigger`;
  const labelId = `${reactId}-label`;
  const valueId = `${reactId}-value`;
  const requiredId = `${reactId}-required`;
  const errorId = error ? `${reactId}-error` : undefined;
  const helpId = help ? `${reactId}-help` : undefined;
  const narrow = useMediaQuery(PHONE_QUERY);
  const phone = face ? face === 'phone' : narrow;
  const labelText = typeof label === 'string' ? label : (ariaLabel ?? '');
  const hasValue = chosen.length > 0;
  const tags = props.multiple === true && props.selectedDisplay === 'tags';
  const none = props.multiple === true && props.selectedDisplay === 'none';
  const showClear = clearable && hasValue && !disabled && !none && props.onClear;

  // A printable key on the closed field opens it with the letter forwarded (ADR-080 §11).
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (open || disabled) return;
    if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      onOpenChange(true, e.key);
    }
  };

  // The closed face's text.
  const nameOf = (c: PickerChosen) => (c.unavailable ? t('picker.notAvailable') : c.dormant ? `${c.label} · ${t('picker.dormant')}` : c.inactive ? `${c.label} · ${t('picker.inactive')}` : c.label);
  let faceText: ReactNode;
  // The face text carries `valueId`: the trigger is NAMED by its label + what it shows (the chosen name · the count · the placeholder).
  if (!hasValue || tags || none) faceText = <span id={valueId} className="truncate text-[var(--color-text-disabled)]">{placeholder ?? t('picker.placeholder')}</span>;
  else if (!props.multiple || chosen.length === 1) {
    const one = chosen[0]!;
    // One value: its name — and, for a deep tree's pick, its path in small text under it (ADR-080 §14).
    faceText = one.description && !one.unavailable ? (
      <span id={valueId} className="min-w-0 flex-1">
        <span className="block truncate">{nameOf(one)}</span>
        <span className="block truncate text-xs text-[var(--color-text-secondary)]">{one.description}</span>
      </span>
    ) : (
      <span id={valueId} className="truncate">{nameOf(one)}</span>
    );
  } else faceText = <span id={valueId} className="truncate">{t('picker.selectedCount', { count: chosen.length })}</span>;

  const trigger = (
    <AriaButton
      id={buttonId}
      className={triggerBase}
      isDisabled={disabled}
      data-invalid={error ? true : undefined}
      aria-label={label ? undefined : ariaLabel}
      // Named by the label (or its own aria-label) + the required word + the value it shows — never the label alone (SC 4.1.2; a button cannot carry `aria-required`).
      aria-labelledby={[label ? labelId : buttonId, required ? requiredId : null, valueId].filter(Boolean).join(' ')}
      aria-describedby={[errorId, helpId].filter(Boolean).join(' ') || undefined}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-field={name}
      onKeyDown={onKeyDown}
      onPress={phone ? () => onOpenChange(true) : undefined}
    >
      {faceText}
      {showClear && <span aria-hidden className="w-7 shrink-0" />}
      <icons.caretDown aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)]" />
    </AriaButton>
  );

  const clear = showClear ? (
    <button
      type="button"
      aria-label={t('select.clear')}
      onClick={() => props.onClear?.()}
      className={
        'absolute end-8 top-1/2 -translate-y-1/2 p-1.5 min-w-[1.75rem] min-h-[1.75rem] inline-flex items-center justify-center rounded-md ' +
        'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] outline-none focus-ring-inset'
      }
    >
      <icons.close aria-hidden size={14} />
    </button>
  ) : null;

  // The chips below a pick-many form field — one ✕ each, folded past CHIPS_SHOWN.
  const chips =
    tags && hasValue ? (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <TagGroup
          ariaLabel={props.multiple === true ? (props.selectedItemsLabel ?? t('select.selectedItems')) : t('select.selectedItems')}
          items={chosen.slice(0, CHIPS_SHOWN).map((c) => ({ id: c.id, label: nameOf(c) }))}
          onRemove={props.multiple === true && props.onRemove ? props.onRemove : undefined}
          disabled={disabled}
        />
        {chosen.length > CHIPS_SHOWN && (
          <Button variant="link" onClick={() => onOpenChange(true)} disabled={disabled}>
            {t('picker.moreChips', { count: chosen.length - CHIPS_SHOWN })}
          </Button>
        )}
      </div>
    ) : null;

  const close = () => onOpenChange(false);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label id={labelId} htmlFor={buttonId} className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
          {label}
          {required && (
            <span aria-hidden="true" className="ms-0.5 text-[var(--color-danger)]">
              *
            </span>
          )}
        </label>
      )}
      {/* The programmatic "required" — a visually hidden word in the trigger's name (the asterisk is decorative). */}
      {required && (
        <span id={requiredId} className="sr-only">
          {t('form.required')}
        </span>
      )}
      {phone ? (
        <>
          <div className="relative">
            {trigger}
            {clear}
          </div>
          <Sheet open={open} onClose={close} title={labelText} face={phoneFace} endAction={phoneFace === 'full' ? <Button variant="ghost" onClick={close}>{t('picker.done')}</Button> : undefined}>
            {open && children({ close, face: 'phone' })}
          </Sheet>
        </>
      ) : (
        <DialogTrigger isOpen={open} onOpenChange={(o) => onOpenChange(o)}>
          <div className="relative">
            {trigger}
            {clear}
          </div>
          <AriaPopover
            placement="bottom start"
            offset={4}
            className={
              'z-dropdown flex max-h-[min(26rem,calc(100dvh-5rem))] w-[max(var(--trigger-width),20rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-md border border-[var(--color-border)] ' +
              'bg-[var(--color-bg-overlay)] shadow-overlay data-[entering]:o-pop-in data-[exiting]:o-pop-out'
            }
          >
            <Dialog aria-label={labelText} className="flex min-h-0 flex-1 flex-col p-1.5 outline-none">
              {children({ close, face: 'desk' })}
            </Dialog>
          </AriaPopover>
        </DialogTrigger>
      )}
      {chips}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
      {help && (
        <p id={helpId} className="mt-1 text-xs text-[var(--color-text-secondary)]">
          {help}
        </p>
      )}
    </div>
  );
}
